import { useNuxtApp, useAuth } from "#imports";

type Method = 'get' | 'post' | 'put' | 'delete' | 'patch';

interface AxiosRequestConfig {
  url: string;
  method?: Method;
  headers?: Record<string, string>;
  params?: Record<string, any>;
  data?: any;
  responseType?: "arrayBuffer" | "blob" | "json" | "text";
}

interface AxiosResponse<T = any> {
  data: T;
  status: number;
  statusText: string;
  headers: Record<string, string>;
  config: AxiosRequestConfig;
  request?: any;
}

const extractMessage = (body: any): string | null => {
  if (typeof body === "string" && body.trim()) return body;
  if (body && typeof body === "object") {
    return body.Message || body.message || body.detail || body.title || null;
  }
  return null;
};

export class AxiosService {
  private nuxtApp: ReturnType<typeof useNuxtApp>;
  private getAuth?: () => ReturnType<typeof useAuth>;

  constructor(
    nuxtApp: ReturnType<typeof useNuxtApp>,
    getAuth?: () => ReturnType<typeof useAuth>
  ) {
    this.nuxtApp = nuxtApp;
    this.getAuth = getAuth;
  }

  async request<T = any>(config: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    const { $utils } = this.nuxtApp || {};
    let { url, method = 'get', headers, params, data } = config;

    // Construct the full URL with query parameters
    const queryString = params
      ? '?' + new URLSearchParams(params).toString()
      : '';
    const fullUrl = url + queryString;

    if (method !== 'get' && method !== 'delete') {
      headers = {
        'Content-Type': 'application/json',
        ...headers
      }
    }

    // Add the Authorization header if the URL is in the list of hosts that require it
    const authHeaderHosts = this.nuxtApp?.$config.public.authHeaderHosts || [];
    let hostname = "";
    try {
      if (!url.startsWith('/api/rg3p')) {
        hostname = new URL(url).hostname; 
      }
    } catch {
      console.error(`Invalid URL: ${url}`);
    }
    if (authHeaderHosts.some((authHeaderHost) => hostname.includes(authHeaderHost))) {
      const auth = this.getAuth?.();
      const sessionData = auth?.data?.value as any;
      if (auth?.status?.value === "authenticated" && sessionData?.accessToken) {
        headers = {
          ...headers,
          Authorization: `Bearer ${sessionData.accessToken}`,
        };
      }
    }

    // Set up the fetch options
    const fetchOptions: RequestInit = {
      method: method.toUpperCase(),
      headers: headers,
      body: method !== 'get' && method !== 'delete' ? JSON.stringify(data) : undefined,
    };

    // Perform the fetch request
    const response = await fetch(fullUrl, fetchOptions);

    // Parse the response by responseType
    let responseData = null;
    if (response.status !== 204) {
      try {
        if (config.responseType === "blob") {
          responseData = await response.blob();
        } else if (config.responseType === "arrayBuffer") {
          responseData = await response.arrayBuffer();
        } else {
          const text = await response.text(); // get raw body
          try {
            responseData = text ? JSON.parse(text) : null; // parse only if not empty
          } catch {
            responseData = text || null; // fall back to raw text for non-JSON bodies (e.g. plain string error messages)
          }
        }
      } catch (error) {
        $utils?.devError?.(`Error parsing JSON: ${error}`);
      }
    }

    // Trigger catch behavior of callers, if the response is not OK
    if (!response.ok) {
      const statusText = response.statusText || `HTTP ${response.status}`;

      throw {
        error: extractMessage(responseData) || statusText || "Unknown error",
        status: response.status,
        response: {
          data: responseData,
        },
      };
    }

    // Construct the Axios-like response object
    const axiosResponse: AxiosResponse<T> = {
      data: responseData,
      status: response.status,
      statusText: response.statusText,
      headers: this.parseHeaders(response.headers),
      config: config,
      request: response,
    };

    return axiosResponse;
  }

  get<T = any>(url: string, config?: Omit<AxiosRequestConfig, 'url' | 'method'>): Promise<AxiosResponse<T>> {
    return this.request<T>({ ...config, url, method: 'get' });
  }

  post<T = any>(url: string, data?: any, config?: Omit<AxiosRequestConfig, 'url' | 'method' | 'data'>): Promise<AxiosResponse<T>> {
    return this.request<T>({ ...config, url, method: 'post', data });
  }

  put<T = any>(url: string, data?: any, config?: Omit<AxiosRequestConfig, 'url' | 'method' | 'data'>): Promise<AxiosResponse<T>> {
    return this.request<T>({ ...config, url, method: 'put', data });
  }

  patch<T = any>(url: string, data?: any, config?: Omit<AxiosRequestConfig, 'url' | 'method' | 'data'>): Promise<AxiosResponse<T>> {
    return this.request<T>({ ...config, url, method: 'patch', data });
  }

  delete<T = any>(url: string, config?: Omit<AxiosRequestConfig, 'url' | 'method'>): Promise<AxiosResponse<T>> {
    return this.request<T>({ ...config, url, method: 'delete' });
  }

  // Helper method to parse headers into a plain object
  private parseHeaders(headers: Headers): Record<string, string> {
    const result: Record<string, string> = {};
    headers.forEach((value, key) => {
      result[key] = value;
    });
    return result;
  }
}
